import React, { useCallback } from 'react';
import { Button as BootstrapButton } from 'react-bootstrap';

import Button from 'components/DesignSystem/Button';
import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import { AMPERE_COMPANY_ID } from 'helper/flux/myStudiesFilters';
import useMigrationNotice from 'utils/hooks/flux/personalized/useMigrationNotice';
import { MyStudiesFiltersChange } from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

export interface MigrationNoticeProps {
  /** Papel de quem vê: o aviso é só do admin, que tinha a caixa "Casos Ampere". */
  isAdmin: boolean;
  /** Id do usuário logado (`selectUserId`); com -1 (sem JWT) o aviso espera o login. */
  userId: number;
  /** `setFilters` do `useMyStudiesFilters`: recebe a empresa 1 de "Mostrar casos Ampere". */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
  /**
   * Chamado depois das duas dispensas ("Entendi" e "Mostrar casos Ampere"). O aviso some com o
   * botão que tinha o foco, que cairia no `<body>` (WCAG 2.4.3): a #1999 leva o foco ao campo de
   * busca da barra.
   */
  onDismiss?: () => void;
}

/**
 * Aviso de migração da antiga caixa "Casos Ampere" para o filtro Empresa (#1997, .dc.html:123-128;
 * nota 3 do design em .dc.html:45-47), só para admin, acima da barra de filtros.
 *
 * Controlado por props, sem chamar o `useMyStudiesFilters`: a #1999 o monta na zona `notice`, com
 * o `setFilters` da instância única do hook e um `onDismiss` que leva o foco ao campo de busca.
 * - "Mostrar casos Ampere" pede `{ empresa: { companyId: 1 } }` e dispensa. Com um usuário
 *   específico em Criado por, o `applyFilterChange` o troca por Todos (regra do dropdown de
 *   empresa, .dc.html:666; o `migApply` do protótipo, .dc.html:709, só troca a empresa).
 * - "Entendi" só dispensa.
 * - Quando aparece e quando some (D5): `useMigrationNotice`.
 */
export default function MigrationNotice({
  isAdmin,
  userId,
  onFiltersChange,
  onDismiss,
}: MigrationNoticeProps) {
  const { storage } = useMyStudiesProduct();
  const { visible, dismiss } = useMigrationNotice({
    userId, isAdmin, storagePrefix: storage.noticePrefix,
  });

  const dismissNotice = useCallback(() => {
    dismiss();
    onDismiss?.();
  }, [dismiss, onDismiss]);

  const showAmpereCases = useCallback(() => {
    onFiltersChange({ empresa: { companyId: AMPERE_COMPANY_ID } });
    dismissNotice();
  }, [onFiltersChange, dismissNotice]);

  if (!visible) return null;

  return (
    <div className="my-studies-migration-notice" data-testid="my-studies-migration-notice">
      <p className="my-studies-migration-notice-text">
        A caixa
        {' '}
        <strong>“Casos Ampere”</strong>
        {' '}
        agora está no filtro
        {' '}
        <strong>Empresa</strong>
        . Para ver só os estudos da Ampere, escolha “Casos Ampere” na lista.
      </p>
      <Button type="primary" small onClick={showAmpereCases}>Mostrar casos Ampere</Button>
      <BootstrapButton
        variant="link"
        className="my-studies-migration-notice-dismiss"
        onClick={dismissNotice}
      >
        Entendi
      </BootstrapButton>
    </div>
  );
}
